import PlacesAutocomplete from "stimulus-places-autocomplete";
// Connects to data-controller="places"
export default class PlacesController extends PlacesAutocomplete {
  static targets = [
    "address",
    "city",
    "streetNumber",
    "route",
    "postalCode",
    "country",
    "county",
    "state",
    "longitude",
    "latitude",
    "citySearch",
    "locationError"
  ];

  connect() {
    super.connect();

    if (!globalThis.gm_authFailure) {
      globalThis.gm_authFailure = () => {
        this.showLocationError("We’re having trouble loading location search. Please try again in a moment.");
        this.enableLocationInput();
      };
    }
  }

  placeChanged() {
    this.hideLocationError();
    super.placeChanged();

    if (!this.place?.geometry) {
      this.addressTarget.dataset.placeSelected = "false";
      return;
    }

    let addressComponents = this.place.address_components;

    let city = addressComponents.find(component => component.types.includes("locality"))?.long_name || "";
    let country = addressComponents.find(component => component.types.includes("country"))?.long_name || "";
    let state = addressComponents.find(component => component.types.includes("administrative_area_level_1"))?.long_name || "";
    let lat = this.place.geometry?.location.lat() || "";
    let lng = this.place.geometry?.location.lng() || "";

    let location = {
      city: city,
      country: country,
      state: state,
      lat: lat,
      lng: lng,
      place: this.addressTarget?.value
    };

    this.addressTarget.dataset.placeSelected = "true";
    this.addressTarget.dataset.selectedValue = this.addressTarget.value;

    const otherController = this.application.getControllerForElementAndIdentifier(document.getElementById("new-audience-form"), "audience");
    if (otherController !== null) {
      otherController.setLocalLocationFormData(location);
    }
    this.flyToLocation(lat, lng);

    this.addressTarget.dispatchEvent(
      new CustomEvent("location-selected", { bubbles: true })
    );
  }

  addressTargetConnected(e) {
    super.connect();
  }

  changeCity(e) {
    const input = this.addressTarget;
    if (this.cityAutocomplete) {
      return;
    }
    this.cityAutocomplete = new google.maps.places.Autocomplete(input);
    this.cityAutocomplete.addListener("place_changed", () => {
      const place = this.cityAutocomplete.getPlace();
      if (!place) {
        return;
      }

      // Only allow geographical areas.
      const allowedAreaTypes = new Set([
        "locality",
        "sublocality",
        "political",
        "sublocality_level_1",
        "sublocality_level_2",
        "administrative_area_level_2",
        "administrative_area_level_3"
      ]);
      const isArea = place.types?.some(type =>
        allowedAreaTypes.has(type)
      );

      // Don't accept stadium, restaurant, mall, etc.
      if (!isArea) {
        this.showLocationError("We’re having trouble loading location search. Please try again in a moment.");
        document.getElementById("aud-location-error").innerHTML = "Please select an area or city, not a specific location.";
        input.value = "";
        return;
      }

      const addressComponents = place.address_components || [];

      const city =
        addressComponents.find(component =>
          component.types.includes("locality")
        )?.long_name || "";

      const country =
        addressComponents.find(component =>
          component.types.includes("country")
        )?.long_name || "";

      const state =
        addressComponents.find(component =>
          component.types.includes("administrative_area_level_1")
        )?.long_name || "";
      const lat = place.geometry?.location?.lat() || "";
      const lng = place.geometry?.location?.lng() || "";
      const location = {
        city: city,
        country: country,
        state: state,
        lat: lat,
        lng: lng,
        place: place.name || place.formatted_address || input.value
      };
      const form = document.getElementById("new-audience-form");
      const otherController = form
        ? this.application.getControllerForElementAndIdentifier(
          form,
          "audience"
        )
        : null;
      if (!otherController) {
        return;
      }
      otherController.setlocationFormData(location);
      input.value = "";
      this.hideLocationError();
      document.getElementById("aud-location-error").innerHTML = "";
    });
  }

  flyToLocation(lat, lng) {
    const mapController = this.application.getControllerForElementAndIdentifier(
      document.getElementById("local"),
      "google-map"
    );
    if (mapController) {
      mapController.flyToLocation(lat, lng);
    } else {
      console.error("Google Map controller not found.");
    }
  }

  updateRadius(event) {
    const mapController = this.application.getControllerForElementAndIdentifier(
      document.getElementById("local"),
      "google-map"
    );
    mapController?.updateRadius(event);
  }

  inputChanged() {
    this.addressTarget.dataset.placeSelected = "false";
    this.hideLocationError();
  }

  showLocationError(message) {
    if (!this.hasLocationErrorTarget) return;

    this.locationErrorTarget.textContent = message;
    this.locationErrorTarget.classList.remove("d-none");
  }

  hideLocationError() {
    if (!this.hasLocationErrorTarget) return;

    this.locationErrorTarget.textContent = "";
    this.locationErrorTarget.classList.add("d-none");
  }

  enableLocationInput() {
    if (this.hasAddressTarget) {
      this.addressTarget.disabled = false;
      this.addressTarget.removeAttribute("readonly");
      this.addressTarget.style.removeProperty("background-image");
      this.addressTarget.style.removeProperty("background-repeat");
      this.addressTarget.style.removeProperty("background-position");
    }
  }

  disconnect() {
    super.disconnect?.();
  }
}
